import styled, { css, keyframes } from 'styled-components';
import {
  BrighterGreen,
  PrimaryTitleGray,
} from '../../../../app/theme/tokens/colors';

export const BorderColor = '#e6e8e4';
export const MutedText = '#6d6d6d';
export const SubtleText = '#8a8a8a';

const addedPulse = keyframes`
  50% { transform: scale(1.04); }
`;

const cartPulse = keyframes`
  45% { transform: scale(1.06); box-shadow: 0 0 0 8px rgba(49, 131, 0, 0.16); }
`;

const toastIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ---------- Estrutura da página ---------- */

export const PageWrapper = styled.div`
  padding: 20px 24px 40px;
`;

export const CatalogBody = styled.div`
  display: grid;
  grid-template-columns: 232px minmax(0, 1fr);
  gap: 20px;
  align-items: start;

  @media (max-width: 1050px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const SidebarColumn = styled.div`
  position: sticky;
  top: 16px;

  @media (max-width: 1050px) {
    display: none;
  }
`;

export const MobileFiltersBar = styled.div`
  display: none;
  margin-bottom: 14px;

  @media (max-width: 1050px) {
    display: block;
  }
`;

export const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 1500px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (max-width: 1050px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 620px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const StatePanel = styled.div`
  padding: 40px 32px;
  border: 1px solid ${BorderColor};
  border-radius: 10px;
  background: #fff;
  text-align: center;
  color: ${MutedText};
`;

export const LoadingWrapper = styled.div`
  min-height: 45vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* ---------- Toolbar ---------- */

export const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

export const SearchField = styled.div`
  position: relative;
  flex: 1 1 320px;
  min-width: 240px;

  svg {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: ${SubtleText};
    font-size: 17px;
    pointer-events: none;
  }

  input {
    width: 100%;
    height: 42px;
    padding: 0 14px 0 40px;
    border: 1px solid ${BorderColor};
    border-radius: 8px;
    background: #fff;
    color: ${PrimaryTitleGray};
    font-size: 14px;
    font-family: inherit;
    box-sizing: border-box;
    outline: none;
    transition: border-color 160ms ease, box-shadow 160ms ease;

    &::placeholder {
      color: ${SubtleText};
    }
    &:focus {
      border-color: ${BrighterGreen};
      box-shadow: 0 0 0 3px rgba(49, 131, 0, 0.12);
    }
  }
`;

export const ResultsCount = styled.span`
  color: ${MutedText};
  font-size: 14px;
  white-space: nowrap;
`;

/* ---------- Chips de categoria ---------- */

export const ChipsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

export const CategoryChip = styled.button`
  height: 34px;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid ${BorderColor};
  background: #fff;
  color: ${MutedText};
  font-size: 13px;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 160ms ease, color 160ms ease, border-color 160ms ease;

  &:hover {
    border-color: ${BrighterGreen};
    color: ${BrighterGreen};
  }

  ${({ $active }) =>
    $active &&
    css`
      background: ${BrighterGreen};
      border-color: ${BrighterGreen};
      color: #fff;

      &:hover {
        background: #2a6e04;
        color: #fff;
      }
    `}
`;

export const ChipsMoreButton = styled.button`
  height: 34px;
  padding: 0 12px;
  border: 0;
  background: transparent;
  color: ${BrighterGreen};
  font-size: 13px;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

/* ---------- Sidebar de filtros ---------- */

export const FiltersPanel = styled.div`
  border: 1px solid ${BorderColor};
  border-radius: 10px;
  background: #fff;
  padding: 16px 16px 8px;
`;

export const FiltersHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${BorderColor};

  strong {
    color: ${PrimaryTitleGray};
    font-size: 15px;
  }
`;

export const ClearFiltersButton = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  color: ${BrighterGreen};
  font-size: 13px;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    color: ${SubtleText};
    cursor: default;
    text-decoration: none;
  }
  &:not(:disabled):hover {
    text-decoration: underline;
  }
`;

export const FilterBlock = styled.div`
  padding: 14px 0;
  border-bottom: 1px solid ${BorderColor};

  &:last-child {
    border-bottom: 0;
  }
`;

export const FilterBlockHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${PrimaryTitleGray};
  font-size: 14px;
  font-family: inherit;
  font-weight: 700;
  cursor: pointer;

  svg {
    color: ${SubtleText};
    font-size: 16px;
    flex-shrink: 0;
  }
`;

export const FilterOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  color: ${MutedText};
  font-size: 13px;
  line-height: 1.35;
  cursor: pointer;

  input {
    accent-color: ${BrighterGreen};
    width: 15px;
    height: 15px;
    margin: 0;
    flex-shrink: 0;
    cursor: pointer;
  }

  span {
    min-width: 0;
    overflow-wrap: anywhere;
  }
`;

export const OptionCount = styled.span`
  color: ${SubtleText};
`;

export const ShowMoreButton = styled.button`
  margin-top: 10px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${BrighterGreen};
  font-size: 13px;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const EmptyFilterHint = styled.p`
  margin: 8px 0 0;
  color: ${SubtleText};
  font-size: 13px;
`;

/* ---------- Card de produto ---------- */

export const Card = styled.div`
  position: relative;
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  border: 1px solid
    ${({ $selected }) => ($selected ? BrighterGreen : BorderColor)};
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
  cursor: pointer;
  transition: transform 180ms ease, box-shadow 180ms ease,
    border-color 180ms ease;

  ${({ $selected }) =>
    $selected &&
    css`
      box-shadow: 0 0 0 2px rgba(49, 131, 0, 0.12);
    `}

  ${({ $selected }) =>
    $selected &&
    css`
      box-shadow: 0 0 0 2px rgba(49, 131, 0, 0.12);
    `}

  &:hover {
    transform: translateY(-3px);
    border-color: ${BrighterGreen};
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  }
`;

export const CardImage = styled.div`
  height: 190px;
  padding: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  box-sizing: border-box;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding: 0 16px 16px;
`;

export const CardCategory = styled.span`
  display: block;
  color: ${BrighterGreen};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CardName = styled.strong`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-top: 6px;
  min-height: 40px;
  color: ${PrimaryTitleGray};
  font-size: 15px;
  line-height: 1.35;
`;

export const CardSku = styled.span`
  display: block;
  margin-top: 6px;
  color: ${SubtleText};
  font-size: 12px;
`;

export const CardDescription = styled.p`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 8px 0 0;
  min-height: 38px;
  color: ${MutedText};
  font-size: 13px;
  line-height: 1.45;
`;

export const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
`;

export const OutlineAddButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 34px;
  padding: 0 16px;
  border: 1px solid ${BorderColor};
  border-radius: 999px;
  background: #fff;
  color: ${MutedText};
  font-size: 13px;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: background 160ms ease, color 160ms ease, border-color 160ms ease;

  svg {
    font-size: 15px;
  }

  &:hover {
    border-color: ${BrighterGreen};
    color: ${BrighterGreen};
    background: #f3f8f0;
  }

  ${({ $added }) =>
    $added &&
    css`
      border-color: ${BrighterGreen};
      background: ${BrighterGreen};
      color: #fff;
      animation: ${addedPulse} 420ms ease;

      &:hover {
        background: ${BrighterGreen};
        color: #fff;
      }
    `}
`;

/* ---------- Carrinho e feedback ---------- */

export const CartButton = styled.button`
  position: fixed;
  right: 28px;
  bottom: 28px;
  z-index: 1200;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: ${BrighterGreen};
  color: #fff;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 7px 20px rgba(33, 102, 15, 0.32);
  transition: background 160ms ease, transform 180ms ease, box-shadow 180ms ease;

  svg {
    color: #fff;
    font-size: 22px;
  }

  &:hover {
    background: #1f5f08;
    box-shadow: 0 9px 24px rgba(33, 102, 15, 0.38);
    transform: translateY(-2px);
  }

  span {
    position: absolute;
    top: -4px;
    right: -4px;
    display: grid;
    place-items: center;
    min-width: 21px;
    height: 21px;
    padding: 0 4px;
    border: 2px solid #fff;
    border-radius: 999px;
    background: #fff;
    color: ${BrighterGreen};
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    box-sizing: border-box;
  }

  ${({ $pulsing }) =>
    $pulsing &&
    css`
      animation: ${cartPulse} 620ms ease;
    `}
`;

export const AddFeedback = styled.div`
  position: fixed;
  right: 27px;
  bottom: 98px;
  z-index: 1101;
  display: flex;
  align-items: center;
  gap: 7px;
  max-width: min(360px, calc(100vw - 40px));
  padding: 11px 14px;
  border-radius: 8px;
  background: #1f5f08;
  color: #fff;
  box-shadow: 0 5px 18px rgba(0, 0, 0, 0.2);
  font-size: 13px;
  font-weight: 600;
  animation: ${toastIn} 180ms ease-out;
`;

export const SkuTag = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 25px;
  width: fit-content;
  padding: 0 9px;
  border: 1px solid #d7d7d7;
  border-radius: 999px;
  background: #f5f5f5;
  color: #626262;
  font-size: 12px;
  font-weight: 700;
  box-sizing: border-box;
`;

export const CategoryTag = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 25px;
  width: fit-content;
  max-width: 100%;
  padding: 0 10px;
  border: 1px solid #bddcac;
  border-radius: 999px;
  background: #eef7e9;
  color: #2f7e0c;
  font-size: 12px;
  font-weight: 700;
  box-sizing: border-box;
`;

export const TagsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  max-width: 100%;
  flex-wrap: wrap;
`;
